import {
  BodyText,
  Heading,
  Switch,
  TabGroup,
  TabPanel,
} from '@postscript/components';
import React, { useState } from 'react';
import styled from 'styled-components';
import { Color, ColorTable, TOKEN_BLOCKS } from './ColorConstants';
import { MediaQueries } from './MainLayout';
import { StyledTabPanelText } from './SimpleStyledComponents';
import Swatch from './Swatch';
import TokenBlock from './TokenBlock';

export interface ColorWidgetProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
}

/* -- COLORS -- */

const ColorHeading = styled(Heading)`
  padding-bottom: var(--spacing-1);
  text-align: center;
`;

const ColorArea = styled.div<{ color: string }>`
  display: flex;
  justify-content: center;
  margin: 0;
  position: relative;
`;

const TableColor = ({ name, hue, core, suggested, muppet }: Color) => (
  <>
    <ColorHeading size="xx-small">
      {muppet} {name}
    </ColorHeading>
    <ColorArea color={hue}>
      {Array.from(Array(9)).map((_, i) => {
        const value = i + 1;
        return (
          <Swatch
            className={`swatch ${
              suggested.includes(value) || value === core
                ? 'suggested-color'
                : ''
            }`}
            core={value === core}
            cssVar={value === core ? `--${hue}-core` : `--${hue}-${i + 1}`}
            hue={hue}
            number={value}
            key={`${hue}-${value}-swatch`}
          />
        );
      })}
    </ColorArea>
  </>
);

/* -- GRAYS -- */

const CompiledGray = ({ index }: { index: number }) => {
  const isSuggested = ![2, 4, 6, 8].includes(index);

  const getCSSVar = () => {
    if (index === 10) return '--black';
    if (index === 0) return '--white';
    return `--gray-${index}`;
  };

  return (
    <Swatch
      className={isSuggested ? 'suggested-color swatch' : 'swatch'}
      contrastBorder={index < 3}
      core={index === 5}
      cssVar={getCSSVar()}
      hue="gray"
      key={`gray-${index.toString()}-swatch`}
      number={index}
    />
  );
};

/* -- BUILD GRID -- */

const StyledColorGrid = styled.div`
  display: flex;
  flex-direction: column;

  &.only-suggested {
    .swatch:not(.suggested-color) {
      pointer-events: none;

      > div:first-child {
        background: transparent;
        border: 1px solid var(--border-color-dim);
        opacity: 0.5;
      }
    }
  }
`;

const StyledPaletteTabPanel = styled(TabPanel)`
  position: relative;
`;

const StyledSwitchContainer = styled.div`
  ${MediaQueries.desktopOnly} {
    position: absolute;
    top: -54px;
    right: 0;
  }
  ${MediaQueries.tabletAndPhone} {
    margin: var(--spacing-2) 0 var(--spacing-4);
  }
`;

const StyledColorWidget = styled.div`
  ${MediaQueries.desktopOnly} {
    min-width: 600px;
  }
`;

const ColorWidget = React.forwardRef<HTMLDivElement, ColorWidgetProps>(
  ({ ...props }, ref) => {
    const [hideNonSuggested, setHideNonSuggested] = useState(true);
    const [currentColorDetailPanel, setCurrentColorDetailPanel] =
      useState('tokens');

    return (
      <StyledColorWidget ref={ref} {...props}>
        <TabGroup
          description="Color details"
          id="color-detail-tablist"
          style={{ marginBottom: 'var(--spacing-4)' }}
        >
          <button
            data-tabpanel="color-tokens"
            data-selected={currentColorDetailPanel === 'tokens'}
            onClick={() => {
              setCurrentColorDetailPanel('tokens');
            }}
            type="button"
          >
            Tokens and CSS Variables
          </button>
          <button
            data-tabpanel="color-full-palette"
            data-selected={currentColorDetailPanel === 'full-palette'}
            onClick={() => {
              setCurrentColorDetailPanel('full-palette');
            }}
            type="button"
          >
            Full Palette
          </button>
        </TabGroup>

        <StyledPaletteTabPanel
          id="color-full-palette"
          selected={currentColorDetailPanel === 'full-palette'}
        >
          <StyledSwitchContainer>
            <Switch
              checked={hideNonSuggested}
              id="toggle-suggested-colors"
              label="Show only suggested"
              onChange={(e) => {
                setHideNonSuggested(e.target.checked);
              }}
              fieldSize="small"
            />
          </StyledSwitchContainer>

          <StyledColorGrid className={hideNonSuggested ? 'only-suggested' : ''}>
            <ColorHeading size="xx-small">Grayscale</ColorHeading>
            <ColorArea color="grays">
              {Array.from(Array(11)).map((_, i) => (
                <CompiledGray key={`${i.toString()}`} index={i} />
              ))}
            </ColorArea>
            {ColorTable.map(({ hue, ...rest }) => (
              <TableColor key={hue} hue={hue} {...rest} />
            ))}
          </StyledColorGrid>
        </StyledPaletteTabPanel>

        <TabPanel
          id="color-tokens"
          selected={currentColorDetailPanel === 'tokens'}
        >
          <StyledTabPanelText>
            <BodyText size="small">
              Color tokens correspond to global CSS variables and have assigned
              light and dark mode values. In Figma, those values are indicated
              by a 🌙 icon for dark mode, but in code they&apos;ll change
              automatically. Tokens cover common use cases and have an implied
              purpose, indicated by their name. Don&apos;t use token colors for
              non-sematic elements (ie. don&apos;t use border-color for
              backgrounds).
            </BodyText>
          </StyledTabPanelText>

          {TOKEN_BLOCKS.map(({ name, ...rest }) => (
            <TokenBlock key={name} name={name} {...rest} />
          ))}
        </TabPanel>
      </StyledColorWidget>
    );
  },
);

export default ColorWidget;
